import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import Navbar from '@/components/Navbar';
import { useUser } from '@/context/UserContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Building2, Search, Megaphone, ArrowLeft, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown, BarChart3, FolderOpen, ChevronDown, ChevronRight, Plus, X, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { ActionBadge, EtatBadge, LodeomBadge } from './Dashboard';
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from 'recharts';

const PIE_COLORS = ['#2563eb', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316'];

const StatPieChart = ({ title, data }) => {
  if (!data || data.length === 0) return null;
  const total = data.reduce((s, d) => s + d.value, 0);
  return (
    <div className="bg-white rounded-xl border border-border/50 shadow-sm p-5" data-testid={`pie-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <h3 className="text-sm font-semibold text-gray-700 mb-3 text-center">{title}</h3>
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={45} outerRadius={85} paddingAngle={2} dataKey="value" nameKey="name" label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: '10px' }}>
            {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
          </Pie>
          <Tooltip formatter={(value) => [`${value} (${((value / total) * 100).toFixed(1)}%)`, 'Entreprises']} />
          <Legend wrapperStyle={{ fontSize: '11px' }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

const computeStats = (companies, field, defaultValue) => {
  const counts = {};
  companies.forEach(c => {
    const val = c[field] || defaultValue;
    counts[val] = (counts[val] || 0) + 1;
  });
  return Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
};

const CampaignList = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [allCompanies, setAllCompanies] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('campagnes');
  const navigate = useNavigate();

  const fetchCampaigns = async () => {
    try {
      const token = localStorage.getItem('token');
      const [campRes, compRes] = await Promise.all([
        axios.get(`${API}/campaigns`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/companies`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setCampaigns(campRes.data);
      setAllCompanies(compRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des campagnes');
    }
  };

  useEffect(() => { fetchCampaigns(); }, []);

  const handleGenerate = async () => {
    try {
      setGenerating(true);
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/campaigns/generate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(res.data.message);
      fetchCampaigns();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setGenerating(false);
    }
  };

  const globalActionStats = useMemo(() => computeStats(allCompanies, 'action', 'A appeler'), [allCompanies]);
  const globalEtatStats = useMemo(() => computeStats(allCompanies, 'etat_compte', 'A renseigner'), [allCompanies]);

  const colors = {
    'Campagne RH': 'from-blue-500 to-blue-700',
    'Campagne FIPRO': 'from-emerald-500 to-emerald-700',
    'Campagne PMS': 'from-amber-500 to-amber-700',
    'Grands Comptes Caraibes': 'from-teal-500 to-teal-700',
  };

  return (
    <div className="min-h-screen bg-[#f1f5f9]">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-heading font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }} data-testid="campaigns-title">
              Campagnes
            </h1>
            <p className="text-muted-foreground mt-1">Segmentez vos entreprises par criteres</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-white rounded-lg border border-border/50 p-1 w-fit" data-testid="campaign-tabs">
          <button
            onClick={() => setActiveTab('campagnes')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'campagnes' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-accent'}`}
            data-testid="tab-campagnes"
          >
            Campagnes
          </button>
          <button
            onClick={() => setActiveTab('global')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'global' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-accent'}`}
            data-testid="tab-global"
          >
            Etat Campagne Caraïbes Global
          </button>
        </div>

        {activeTab === 'campagnes' && (
          <>
            {campaigns.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-xl border border-border/50">
                <Megaphone className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground mb-4">Aucune campagne pour le moment</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {campaigns.map(campaign => (
                  <Link key={campaign.id} to={`/campaigns/${campaign.id}`} className="no-underline" data-testid="campaign-card">
                    <div className="bg-white rounded-xl border border-border/50 shadow-sm overflow-hidden hover:shadow-md transition-shadow group">
                      <div className={`bg-gradient-to-r ${colors[campaign.name] || 'from-gray-500 to-gray-700'} p-6`}>
                        <Megaphone className="h-8 w-8 text-white mb-3 opacity-80" />
                        <h2 className="text-xl font-heading font-bold text-white">{campaign.name}</h2>
                        <p className="text-white/80 text-sm mt-1">{campaign.description}</p>
                      </div>
                      <div className="p-5">
                        <div className="flex items-center gap-3">
                          <Building2 className="h-5 w-5 text-muted-foreground" />
                          <span className="text-2xl font-heading font-bold text-gray-900">{campaign.company_count}</span>
                          <span className="text-sm text-muted-foreground">entreprises</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === 'global' && (
          <div data-testid="global-stats">
            <div className="flex items-center gap-3 mb-6">
              <BarChart3 className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-heading font-semibold text-gray-900">Etat Campagne Caraïbes Global</h2>
              <span className="text-sm text-muted-foreground">({allCompanies.length} entreprises)</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <StatPieChart title="Repartition par Action" data={globalActionStats} />
              <StatPieChart title="Repartition par Etat du compte" data={globalEtatStats} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CampaignDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRegion, setFilterRegion] = useState('all');
  const [sortColumn, setSortColumn] = useState('sector');
  const [sortDirection, setSortDirection] = useState('asc');
  const [activeTab, setActiveTab] = useState('entreprises');
  const [openFolders, setOpenFolders] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const [campRes, compRes] = await Promise.all([
          axios.get(`${API}/campaigns/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/campaigns/${id}/companies`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);
        setCampaign(campRes.data);
        setCompanies(compRes.data);
        // Open all folders by default
        const folders = {};
        (campRes.data.grands_comptes || []).forEach(gc => { folders[gc.id] = true; });
        setOpenFolders(folders);
      } catch (error) {
        toast.error('Erreur');
        navigate('/campaigns');
      }
    };
    fetchData();
  }, [id]);

  const hasGrandsComptes = campaign?.grands_comptes?.length > 0;
  const { isAdmin } = useUser();
  const [newGcOpen, setNewGcOpen] = useState(false);
  const [newGcName, setNewGcName] = useState('');
  const [newGcDesc, setNewGcDesc] = useState('');
  const [addCompanyGcId, setAddCompanyGcId] = useState(null);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [companySearchResults, setCompanySearchResults] = useState([]);
  const [searchTimer, setSearchTimer] = useState(null);

  const headers = useCallback(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const refreshCampaign = useCallback(async () => {
    try {
      const [campRes, compRes] = await Promise.all([
        axios.get(`${API}/campaigns/${id}`, { headers: headers() }),
        axios.get(`${API}/campaigns/${id}/companies`, { headers: headers() }),
      ]);
      setCampaign(campRes.data);
      setCompanies(compRes.data);
    } catch {}
  }, [id, headers]);

  const handleCreateGc = async () => {
    if (!newGcName.trim()) return;
    try {
      const res = await axios.post(`${API}/campaigns/${id}/grands-comptes`, { name: newGcName.trim(), description: newGcDesc.trim() }, { headers: headers() });
      toast.success(res.data.message);
      setNewGcOpen(false);
      setNewGcName('');
      setNewGcDesc('');
      const gc = res.data.grand_compte;
      setOpenFolders(prev => ({ ...prev, [gc.id]: true }));
      refreshCampaign();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleUploadLogo = async (gcId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${API}/campaigns/${id}/grands-comptes/${gcId}/logo`, formData, {
        headers: { ...headers(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success(res.data.message);
      refreshCampaign();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur upload logo');
    }
  };

  const handleDeleteGc = async (gcId, gcName) => {
    if (!window.confirm(`Supprimer le grand compte "${gcName}" ? Les entreprises ne seront pas supprimees du CRM.`)) return;
    try {
      await axios.delete(`${API}/campaigns/${id}/grands-comptes/${gcId}`, { headers: headers() });
      toast.success('Grand compte supprime');
      refreshCampaign();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const searchCompaniesForGc = async (q) => {
    if (!q || q.length < 2) { setCompanySearchResults([]); return; }
    try {
      const res = await axios.get(`${API}/companies/search?q=${encodeURIComponent(q)}`, { headers: headers() });
      setCompanySearchResults(res.data);
    } catch {}
  };

  const handleAddCompanyToGc = async (gcId, companyId) => {
    try {
      const res = await axios.post(`${API}/campaigns/${id}/grands-comptes/${gcId}/companies`, { company_ids: [companyId] }, { headers: headers() });
      toast.success(res.data.message);
      setCompanySearchResults(prev => prev.filter(c => c.id !== companyId));
      refreshCampaign();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const handleRemoveCompanyFromGc = async (gcId, companyId) => {
    try {
      await axios.delete(`${API}/campaigns/${id}/grands-comptes/${gcId}/companies/${companyId}`, { headers: headers() });
      toast.success('Entreprise retiree');
      refreshCampaign();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  const filterAndSort = (list) => {
    let filtered = list;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.name?.toLowerCase().includes(term) ||
        c.siren?.includes(searchTerm) ||
        c.sector?.toLowerCase().includes(term) ||
        c.city?.toLowerCase().includes(term)
      );
    }
    if (filterRegion !== 'all') {
      filtered = filtered.filter(c => c.region === filterRegion);
    }
    return [...filtered].sort((a, b) => {
      let valA, valB;
      if (sortColumn === 'size') { valA = a.size || 0; valB = b.size || 0; }
      else { valA = (a[sortColumn] || '').toString().toLowerCase(); valB = (b[sortColumn] || '').toString().toLowerCase(); }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filteredAndSorted = useMemo(() => filterAndSort(companies), [companies, searchTerm, filterRegion, sortColumn, sortDirection]);

  const actionStats = useMemo(() => computeStats(companies, 'action', 'A appeler'), [companies]);
  const etatStats = useMemo(() => computeStats(companies, 'etat_compte', 'A renseigner'), [companies]);
  const lodeomStats = useMemo(() => computeStats(companies, 'bareme_lodeom', 'HL'), [companies]);

  const toggleSort = (col) => {
    if (sortColumn === col) setSortDirection(p => p === 'asc' ? 'desc' : 'asc');
    else { setSortColumn(col); setSortDirection('asc'); }
  };

  const toggleFolder = (folderId) => {
    setOpenFolders(prev => ({ ...prev, [folderId]: !prev[folderId] }));
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDirection === 'asc'
      ? <ArrowUp className="h-3 w-3 ml-1 text-primary" />
      : <ArrowDown className="h-3 w-3 ml-1 text-primary" />;
  };

  const regions = ['Guadeloupe', 'Martinique', 'Guyane', 'Saint-Martin', 'Saint-Barthelemy', 'Metropole (Siege Social)'];

  if (!campaign) return <div className="min-h-screen bg-[#f1f5f9]"><Navbar /><div className="text-center py-12">Chargement...</div></div>;

  const isRH = campaign.name === 'Campagne RH';
  const isFIPRO = campaign.name === 'Campagne FIPRO';
  const hasStats = isRH || isFIPRO;

  const CompanyTable = ({ data }) => (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-accent/30 border-b border-border">
          <tr>
            <th className="text-left px-3 py-3 text-sm font-semibold text-gray-700 cursor-pointer select-none hover:bg-accent/50" onClick={() => toggleSort('name')}>
              <span className="inline-flex items-center">Entreprise <SortIcon column="name" /></span>
            </th>
            <th className="text-left px-3 py-3 text-sm font-semibold text-gray-700">SIREN</th>
            <th className="text-left px-3 py-3 text-sm font-semibold text-gray-700 cursor-pointer select-none hover:bg-accent/50" onClick={() => toggleSort('sector')}>
              <span className="inline-flex items-center">NAF <SortIcon column="sector" /></span>
            </th>
            <th className="text-left px-3 py-3 text-sm font-semibold text-gray-700 cursor-pointer select-none hover:bg-accent/50" onClick={() => toggleSort('size')}>
              <span className="inline-flex items-center">Eff. <SortIcon column="size" /></span>
            </th>
            <th className="text-left px-3 py-3 text-sm font-semibold text-gray-700 cursor-pointer select-none hover:bg-accent/50" onClick={() => toggleSort('region')}>
              <span className="inline-flex items-center">Region <SortIcon column="region" /></span>
            </th>
            <th className="text-left px-3 py-3 text-sm font-semibold text-gray-700 cursor-pointer select-none hover:bg-accent/50" onClick={() => toggleSort('action')}>
              <span className="inline-flex items-center">Action <SortIcon column="action" /></span>
            </th>
            <th className="text-left px-3 py-3 text-sm font-semibold text-gray-700 cursor-pointer select-none hover:bg-accent/50" onClick={() => toggleSort('etat_compte')}>
              <span className="inline-flex items-center">Etat <SortIcon column="etat_compte" /></span>
            </th>
            <th className="text-left px-3 py-3 text-sm font-semibold text-gray-700 cursor-pointer select-none hover:bg-accent/50" onClick={() => toggleSort('bareme_lodeom')}>
              <span className="inline-flex items-center">LODEOM <SortIcon column="bareme_lodeom" /></span>
            </th>
          </tr>
        </thead>
        <tbody>
          {data.map((company) => (
            <tr key={company.id} className="border-b border-border/50 hover:bg-accent/20 transition-colors group" data-testid="campaign-company-row">
              <td className="px-3 py-2">
                <Link to={`/companies/${company.id}`} className="flex items-center gap-2 no-underline" data-testid="campaign-company-link">
                  <div className="p-1.5 bg-accent rounded-lg">
                    <Building2 className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <span className="font-semibold text-gray-900 text-sm group-hover:text-primary transition-colors">{company.name}</span>
                  {company.tags && company.tags.map(tag => (
                    <span key={tag} className="text-xs text-orange-600 font-medium">{tag}</span>
                  ))}
                </Link>
              </td>
              <td className="px-3 py-2 text-xs text-gray-600">{company.siren}</td>
              <td className="px-3 py-2">
                <span className="text-[10px] font-mono px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded">{company.sector || '-'}</span>
              </td>
              <td className="px-3 py-2 text-xs text-gray-600">
                {company.size}
                {company.tranche_effectif && <span className="text-[10px] text-muted-foreground ml-0.5">(T{company.tranche_effectif})</span>}
              </td>
              <td className="px-3 py-2">
                <span className="text-[10px] px-1.5 py-0.5 bg-primary/10 text-primary rounded-full font-medium">{company.region}</span>
              </td>
              <td className="px-3 py-2"><ActionBadge value={company.action} /></td>
              <td className="px-3 py-2"><EtatBadge value={company.etat_compte} /></td>
              <td className="px-3 py-2"><LodeomBadge value={company.bareme_lodeom} alt={company.bareme_lodeom_alt} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f1f5f9]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/campaigns')} className="gap-2" data-testid="back-to-campaigns">
            <ArrowLeft className="h-4 w-4" /> Campagnes
          </Button>
        </div>

        <div className="mb-6">
          <h1 className="text-3xl font-heading font-bold text-gray-900" data-testid="campaign-detail-title">{campaign.name}</h1>
          <p className="text-muted-foreground mt-1">{campaign.description}</p>
          <div className="flex items-center gap-2 mt-3">
            <span className="text-sm font-semibold text-primary bg-primary/10 px-3 py-1 rounded-full">
              {campaign.company_count} entreprises
            </span>
          </div>
        </div>

        {/* Tabs */}
        {hasStats && (
          <div className="flex gap-1 mb-6 bg-white rounded-lg border border-border/50 p-1 w-fit" data-testid="campaign-detail-tabs">
            <button
              onClick={() => setActiveTab('entreprises')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'entreprises' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-accent'}`}
              data-testid="tab-entreprises"
            >
              Entreprises
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'stats' ? 'bg-primary text-white' : 'text-gray-600 hover:bg-accent'}`}
              data-testid="tab-stats"
            >
              Etat de la campagne
            </button>
          </div>
        )}

        {activeTab === 'stats' && hasStats && (
          <div data-testid="campaign-stats">
            <div className="flex items-center gap-3 mb-6">
              <BarChart3 className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-heading font-semibold text-gray-900">Statistiques - {campaign.name}</h2>
            </div>
            <div className={`grid grid-cols-1 ${isRH ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
              {isRH && <StatPieChart title="Repartition par Bareme LODEOM" data={lodeomStats} />}
              <StatPieChart title="Repartition par Action" data={actionStats} />
              <StatPieChart title="Repartition par Etat du compte" data={etatStats} />
            </div>
          </div>
        )}

        {activeTab === 'entreprises' && (
          <>
            {/* Search & filter */}
            <div className="bg-white rounded-xl border border-border/50 shadow-sm p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Rechercher par nom, SIREN, secteur NAF, ville..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="campaign-search" />
                </div>
                <Select value={filterRegion} onValueChange={setFilterRegion}>
                  <SelectTrigger className="w-full md:w-[220px]" data-testid="campaign-region-filter">
                    <SelectValue placeholder="Toutes les regions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Toutes les regions</SelectItem>
                    {regions.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Grands Comptes Folders */}
            {(hasGrandsComptes || isAdmin) && (
              <div className="space-y-4">
                {/* Add new Grand Compte button - Admin only */}
                {isAdmin && (
                  <Dialog open={newGcOpen} onOpenChange={setNewGcOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="gap-2 border-dashed border-2 border-teal-300 text-teal-700 hover:bg-teal-50 w-full py-6 rounded-xl" data-testid="add-grand-compte-btn">
                        <Plus className="h-5 w-5" />
                        Ajouter un nouveau grand compte
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle className="font-heading text-lg">Nouveau grand compte</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label>Nom du grand compte</Label>
                          <Input value={newGcName} onChange={(e) => setNewGcName(e.target.value)} placeholder="Ex: SAFO - Groupe Safo" data-testid="gc-name-input" />
                        </div>
                        <div>
                          <Label>Description (optionnel)</Label>
                          <Input value={newGcDesc} onChange={(e) => setNewGcDesc(e.target.value)} placeholder="Description courte..." data-testid="gc-desc-input" />
                        </div>
                        <Button onClick={handleCreateGc} className="w-full rounded-full" data-testid="create-gc-btn">
                          Creer le grand compte
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}

                {(campaign.grands_comptes || []).map(gc => {
                  const gcCompanies = companies.filter(c => gc.company_ids.includes(c.id));
                  const gcFiltered = filterAndSort(gcCompanies);
                  const isOpen = openFolders[gc.id];
                  const isAddingToThis = addCompanyGcId === gc.id;
                  return (
                    <div key={gc.id} className="bg-white rounded-xl border border-border/50 shadow-sm overflow-hidden" data-testid="grand-compte-folder">
                      <div className="flex items-center bg-teal-50 border-b border-border/50">
                        <button
                          className="flex-1 flex items-center gap-4 px-5 py-4 hover:bg-teal-100 transition-colors cursor-pointer"
                          onClick={() => toggleFolder(gc.id)}
                          data-testid="folder-toggle"
                        >
                          {isOpen ? <ChevronDown className="h-5 w-5 text-teal-600" /> : <ChevronRight className="h-5 w-5 text-teal-600" />}
                          {gc.logo_url ? (
                            <img src={`${API.replace('/api', '')}${gc.logo_url}`} alt="" className="h-10 w-10 rounded-lg object-contain bg-white border border-border/50 p-0.5" data-testid="gc-logo" />
                          ) : (
                            <FolderOpen className="h-5 w-5 text-teal-600" />
                          )}
                          <div className="flex-1 text-left">
                            <h3 className="font-heading font-bold text-gray-900 text-lg">{gc.name}</h3>
                            <p className="text-sm text-muted-foreground">{gc.description}</p>
                          </div>
                          <span className="text-sm font-semibold text-teal-700 bg-teal-100 px-3 py-1 rounded-full" data-testid="folder-count">
                            {gcCompanies.length} entreprise{gcCompanies.length > 1 ? 's' : ''}
                          </span>
                        </button>
                        {isAdmin && (
                          <div className="flex items-center gap-1 pr-3">
                            <label className="cursor-pointer text-teal-600 hover:bg-teal-100 rounded-md p-1.5 transition-colors" title="Changer le logo" data-testid="upload-logo-btn">
                              <input type="file" accept="image/*" className="hidden" onChange={(e) => { if (e.target.files[0]) handleUploadLogo(gc.id, e.target.files[0]); e.target.value = ''; }} />
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
                            </label>
                            <Button variant="ghost" size="sm" className="text-teal-700 hover:bg-teal-100 gap-1 text-xs h-8" onClick={(e) => { e.stopPropagation(); setAddCompanyGcId(isAddingToThis ? null : gc.id); setCompanySearchTerm(''); setCompanySearchResults([]); if (!openFolders[gc.id]) toggleFolder(gc.id); }} data-testid="add-company-to-gc-btn">
                              <Plus className="h-3.5 w-3.5" />
                              Ajouter
                            </Button>
                            <Button variant="ghost" size="icon" className="text-red-400 hover:text-red-600 hover:bg-red-50 h-8 w-8" onClick={(e) => { e.stopPropagation(); handleDeleteGc(gc.id, gc.name); }} data-testid="delete-gc-btn">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>

                      {isOpen && (
                        <>
                          {/* Add Company Search - inside the folder */}
                          {isAddingToThis && (
                            <div className="px-5 py-3 bg-blue-50 border-b border-blue-200" data-testid="add-company-search">
                              <div className="flex items-center gap-2 mb-2">
                                <p className="text-sm font-medium text-blue-800 flex-1">Ajouter des entreprises du CRM</p>
                                <Button variant="ghost" size="icon" className="h-6 w-6 text-blue-600" onClick={() => setAddCompanyGcId(null)}>
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                              <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                  placeholder="Rechercher par nom ou SIREN..."
                                  value={companySearchTerm}
                                  onChange={(e) => {
                                    setCompanySearchTerm(e.target.value);
                                    clearTimeout(searchTimer);
                                    setSearchTimer(setTimeout(() => searchCompaniesForGc(e.target.value.trim()), 300));
                                  }}
                                  className="pl-10 bg-white"
                                  data-testid="gc-company-search-input"
                                  autoFocus
                                />
                              </div>
                              {companySearchResults.length > 0 && (
                                <div className="mt-2 max-h-48 overflow-y-auto space-y-1">
                                  {companySearchResults.map(c => {
                                    const alreadyIn = gc.company_ids.includes(c.id);
                                    return (
                                      <div key={c.id} className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm ${alreadyIn ? 'bg-gray-100 text-gray-400' : 'bg-white border border-blue-100 hover:border-blue-300 cursor-pointer'}`}>
                                        <div>
                                          <span className="font-medium">{c.name}</span>
                                          <span className="text-xs text-muted-foreground ml-2">{c.siren} {c.region ? `- ${c.region}` : ''}</span>
                                        </div>
                                        {alreadyIn ? (
                                          <span className="text-xs text-gray-400">Deja ajoutee</span>
                                        ) : (
                                          <Button size="sm" className="h-7 text-xs gap-1" onClick={() => handleAddCompanyToGc(gc.id, c.id)} data-testid="add-company-confirm-btn">
                                            <Plus className="h-3 w-3" /> Ajouter
                                          </Button>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                              {companySearchTerm.length >= 2 && companySearchResults.length === 0 && (
                                <p className="text-xs text-muted-foreground text-center mt-2">Aucun resultat</p>
                              )}
                            </div>
                          )}

                          {gcFiltered.length === 0 ? (
                            <div className="text-center py-8 text-muted-foreground">
                              {isAdmin ? 'Cliquez sur "Ajouter" pour enrichir ce grand compte' : 'Aucune entreprise'}
                            </div>
                          ) : (
                            <CompanyTable data={gcFiltered} gcId={gc.id} />
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {/* Flat table for campaigns without grands comptes */}
            {!hasGrandsComptes && (
              <div className="bg-white rounded-xl border border-border/50 shadow-sm overflow-hidden" data-testid="campaign-companies-table">
                <div className="px-4 py-2 bg-accent/10 border-b border-border/50 text-xs text-muted-foreground">
                  {filteredAndSorted.length} entreprise{filteredAndSorted.length > 1 ? 's' : ''}
                </div>
                {filteredAndSorted.length === 0 ? (
                  <div className="text-center py-12">
                    <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">Aucune entreprise dans cette campagne</p>
                  </div>
                ) : (
                  <CompanyTable data={filteredAndSorted} />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export { CampaignList, CampaignDetail };
